import * as React from 'react';
import * as _ from 'lodash';

import {
  CheckboxVisibility,
  DetailsList,
  IColumn,
  Persona,
  PersonaSize,
  MessageBar,
  MessageBarType,
  Pivot,
  PivotItem,
  IContextualMenuItem,
  Breadcrumb,
  IconButton,
  IDropdownOption,
  Panel,
  PanelType,
  Checkbox,
  Spinner,
} from '@fluentui/react';

import { ROUTES } from '../../config/routes';
import { AgencyData, ConciergeAgent, AgencyAddress, AgencyPayment, AgencyService, SearchAgency, RESOURCE_PERMISSION_TYPES, ServiceConfigMap } from '../../redux/concierge/types';
import { HealAPI } from '../../healApi';
import { PageHeader } from '../../components/core/PageHeader';
import { OkCancelDialog } from '../core/OkCancelDialog';
import { AgencyForm } from './AgencyForm';
import { PARTNER_ADDRESS_TYPE } from '../../config/constants';
import { AddAddressForm } from './AddAddressForm';
import { AddAgentForm } from './AddAgentForm';
import { connect } from 'react-redux';
import { RootState } from '../../redux/types';
import { Dispatch, bindActionCreators } from 'redux';
import { ToastParams } from '../../redux/message/types';
import { onPageDidMount, onPageWillUnmount, AsyncData } from '../../redux/core';
import { toast } from '../../redux/message/actions';
import { setAgencies } from '../../redux/concierge/actions';
import { ServiceOffering } from '../../api/services.types';
import { formatPhoneNumber } from '../../util/PhoneNumber';
import { NavigateFunction } from 'react-router-dom';
import { withRouter } from '../../util/Router';

interface OwnProps {
  navigate: NavigateFunction;
  params: {
    id: string;
    tab?: string;
  };
}

interface StateProps {
  agencies?: SearchAgency[];
  services: ServiceOffering[];
  isFetching: boolean;
}

interface DispatchProps {
  actions: {
    onPageDidMount: (name: string) => void;
    onPageWillUnmount: (name: string) => void;
    setAgencies: (agencies: SearchAgency[]) => void;
    toast: (params: ToastParams) => void;
  };
}

type AgencyProps = OwnProps & StateProps & DispatchProps;

interface AgencyState {
  agentColumns: IColumn[];
  addressColumns: IColumn[];
  serviceColumns: IColumn[];
  searchText: string;
  agents: AsyncData<ConciergeAgent[]>;
  addresses: AsyncData<AgencyAddress[]>;
  payments: AsyncData<AgencyPayment[]>;
  selectedAccount?: ConciergeAgent;
  isSaving: boolean;
  showPanel?: boolean;
  resourceName?: string;
  resourceForm?: JSX.Element;
  pristineAgency?: AgencyData;
  agency?: AgencyData;
}

class Component extends React.Component<AgencyProps, AgencyState> {
  constructor(props: AgencyProps) {
    super(props);

    this.state = {
      agentColumns: this.buildAgentColumns(),
      addressColumns: this.buildAddressColumns(),
      serviceColumns: this.buildServiceColumns(),
      searchText: '',
      isSaving: false,
      agents: { isFetching: false },
      addresses: { isFetching: false },
      payments: { isFetching: false },
    };
  }

  public componentDidMount() {
    const agencyId = this.props.params.id;
    if (agencyId) {
      this.setState({ agents: { isFetching: true }, addresses: { isFetching: true }, payments: { isFetching: true } });
      HealAPI.fetchAgency(agencyId).then((response) => {
        if (response.ok && response.data) {
          this.setState({
            pristineAgency: response.data.data,
            agency: _.cloneDeep(response.data.data),
          });
        }
      });
      this.fetchAgencyDetails();
    }
    this.props.actions.onPageDidMount('Agency');
    this.setResourceElements(this.props);
  }

  public componentWillUnmount() {
    this.props.actions.onPageWillUnmount('Agency');
  }

  public componentDidUpdate(prevProps: AgencyProps) {
    if (prevProps.params.tab !== this.props.params.tab) {
      this.setResourceElements(this.props);
    }
  }

  public render(): JSX.Element {
    const breadcrumb = (
      <Breadcrumb
        items={[
          {
            text: 'Partners',
            key: 'concierge',
            onClick: () => this.props.navigate(ROUTES.concierge, { replace: true }),
          },
          { text: this.agencyName(), key: 'agency' },
        ]}
        // Returning undefined to OnReduceData tells the breadcrumb not to shrink
        onReduceData={() => undefined}
      />
    );

    return (
      <div>
        <PageHeader title={breadcrumb} isLoading={this.props.isFetching || this.state.isSaving} showShareButton={true} actions={{ items: this.getActions() }} />

        <Pivot onLinkClick={this.onPivotLinkClick} selectedKey={this.props.params.tab || 'agents'}>
          <PivotItem headerText="Agents" itemKey="agents">
            {this.renderTabContent(this.state.agents, this.state.agentColumns)}
          </PivotItem>
          <PivotItem headerText="Addresses" itemKey="addresses">
            {this.renderTabContent(this.state.addresses, this.state.addressColumns)}
          </PivotItem>
          <PivotItem headerText="Services" itemKey="services">
            {this.state.pristineAgency === undefined ? <Spinner label="Loading agency data" /> : this.renderServices()}
          </PivotItem>
          <PivotItem headerText="Profile" itemKey="profile">
            {this.state.pristineAgency === undefined ? (
              <Spinner label="Loading agency data" />
            ) : (
              <AgencyForm
                isSaving={this.state.isSaving}
                pristineAgency={this.state.pristineAgency}
                saveAgency={(agencyMetadata) => {
                  const agency = agencyMetadata as AgencyData;
                  this.updateAgency(agency);
                }}
              />
            )}
          </PivotItem>
        </Pivot>
        {this.renderSidePanel()}
      </div>
    );
  }

  private getActions(): IContextualMenuItem[] {
    const items: IContextualMenuItem[] = [];

    if (this.props.params.tab === 'profile') {
      items.push({
        key: 'Delete agency',
        name: 'Delete agency',
        iconProps: { iconName: 'Delete' },
        onClick: () =>
          OkCancelDialog.show({
            title: 'Delete agency',
            subText: 'This will remove all agents, payment methods and address from this agency and delete it. Are you sure you want to continue?',
            onOk: () => this.deleteAgency(this.props.params.id),
          }),
      });
    } else if (this.props.params.tab === 'services') {
      // NOTE - we can remove the blockedServices existence check once the column has propagated to all environments
      let pristineMap: ServiceConfigMap = {};
      let dirtyMap: ServiceConfigMap = {};

      if (this.state.pristineAgency) {
        pristineMap = this.state.pristineAgency.serviceConfigMap;
      }
      if (this.state.agency) {
        dirtyMap = this.state.agency.serviceConfigMap;
      }
      if (!_.isEqual(pristineMap, dirtyMap)) {
        items.push({
          key: 'Save services',
          name: 'Save services',
          iconProps: { iconName: 'Save' },
          onClick: () => {
            if (this.state.agency) {
              this.updateAgency(this.state.agency);
            }
          },
        });
      }
    } else {
      items.push({
        key: 'Add ' + this.state.resourceName?.toLowerCase(),
        name: 'Add ' + this.state.resourceName?.toLowerCase(),
        iconProps: { iconName: 'Add' },
        onClick: () => this.setState({ showPanel: true }),
      });
    }
    return items;
  }

  private renderTabContent(obj: AsyncData<ConciergeAgent[]> | AsyncData<AgencyAddress[]> | AsyncData<AgencyPayment[]>, columns: IColumn[]): JSX.Element | null {
    if (obj.isFetching || !this.state.pristineAgency) {
      return <Spinner label={'Loading ' + this.props.params.tab} />;
    } else if (!obj.data || obj.data?.length === 0) {
      return (
        <MessageBar messageBarType={MessageBarType.info} isMultiline={false} styles={{ root: { marginTop: 25 } }}>
          No {this.props.params.tab} saved for this agency.
        </MessageBar>
      );
    } else {
      return <DetailsList compact={true} checkboxVisibility={CheckboxVisibility.hidden} items={obj.data || []} setKey="set" columns={columns} />;
    }
  }

  private renderServices(): JSX.Element | null {
    const serviceConfigMap = this.state.agency ? this.state.agency.serviceConfigMap : {};
    const agencyServices = this.props.services.map((service) => {
      return {
        ...service,
        isBlocked: serviceConfigMap[service.code]?.isBlocked ?? false,
        basePrice: serviceConfigMap[service.code]?.basePrice,
      };
    });
    return (
      <div>
        <DetailsList compact={true} checkboxVisibility={CheckboxVisibility.hidden} items={agencyServices} setKey="set" columns={this.state.serviceColumns} />
      </div>
    );
  }

  private renderSidePanel(): JSX.Element {
    const { resourceName, resourceForm } = this.state;

    return (
      <Panel isOpen={this.state.showPanel} onDismiss={() => this.setState({ showPanel: false })} type={PanelType.medium} headerText={'Add ' + resourceName}>
        {resourceForm}
      </Panel>
    );
  }

  private setResourceElements(props: AgencyProps) {
    const { tab, id } = props.params;
    let resourceForm: JSX.Element | undefined;
    let resourceName;

    switch (tab) {
      case 'agents':
        resourceForm = <AddAgentForm onSuccess={this.closePanel} agencyId={id} />;
        resourceName = 'Agent';
        break;
      case 'addresses':
        resourceForm = <AddAddressForm onSuccess={this.closePanel} agencyId={id} />;
        resourceName = 'Address';
        break;
    }

    this.setState({ resourceForm, resourceName });
  }

  private closePanel = () => {
    this.setState({ showPanel: false });
    this.fetchAgencyDetails();
  };

  private fetchAgencyDetails = () => {
    const agencyId = this.props.params.id;
    HealAPI.fetchAgents(agencyId).then((response) => {
      const agents = response.data ? response.data.data : [];
      this.setState({ agents: { data: _.sortBy(agents, ['lastName', 'firstName']), isFetching: false } });
    });

    HealAPI.fetchAgencyAddresses(agencyId).then((response) => {
      this.setState({
        addresses: { data: response.data ? response.data.data.filter((a) => _.find(PARTNER_ADDRESS_TYPE, { key: a.addressType })) : [], isFetching: false },
      });
    });

    HealAPI.fetchAgencyPayments(agencyId).then((response) => {
      this.setState({ payments: { data: response.data ? response.data.data : [], isFetching: false } });
    });
  };

  private agencyName = (): string => {
    if (this.state.agency) {
      return this.state.agency.name;
    }
    return 'Loading...';
  };

  private onPivotLinkClick = (item?: PivotItem) => {
    this.props.navigate(`${ROUTES.agency}/${this.props.params.id}/${item!.props.itemKey!}`);
  };

  private deleteAgency(agencyId: string) {
    this.setState({ isSaving: true });
    HealAPI.deleteAgency(agencyId).then((response) => {
      if (response.ok) {
        this.props.actions.toast({
          type: 'success',
          title: 'Success',
          content: 'The agency was successfully deleted',
        });
        this.props.navigate(ROUTES.concierge);
      } else {
        this.props.actions.toast({
          type: 'error',
          title: 'Error',
          content: "The agency couldn't be deleted, please try again later",
        });
      }
      this.setState({ isSaving: false });
    });
  }

  private removeAgencyAddress(agencyId: string, addressId: string) {
    this.setState({ isSaving: true });
    HealAPI.removeAgencyAddress(agencyId, addressId).then((response) => {
      if (response.ok) {
        this.props.actions.toast({
          type: 'success',
          title: 'Success',
          content: 'Address was successfully removed',
        });
        this.fetchAgencyDetails();
      } else {
        this.props.actions.toast({
          type: 'error',
          title: 'Error',
          content: "The address couldn't be removed, please try again later",
        });
      }
      this.setState({ isSaving: false });
    });
  }

  private removeConciergeAgent(agencyId: string, accountId: string, paymentMethods: AgencyPayment[]) {
    this.setState({ isSaving: true });

    let hasError = false;
    const promises = paymentMethods.map(async (p) => {
      return await HealAPI.removeAgencyPayment(agencyId, p.id);
    });

    Promise.all(promises).then((results) => {
      hasError = results.some((r) => r.ok === false);
    });

    if (hasError) {
      this.props.actions.toast({
        type: 'error',
        title: 'Error',
        content: "Some of the payment methods associated with that agent couldn't be removed. Please try to remove them manually in the Payment list.",
      });
    } else {
      HealAPI.removeConciergeAgent(agencyId, accountId).then((response) => {
        if (response.ok) {
          this.props.actions.toast({
            type: 'success',
            title: 'Success',
            content: 'The account and all associated payment methods have successfully been removed',
          });
          this.fetchAgencyDetails();
        } else {
          this.props.actions.toast({
            type: 'error',
            title: 'Error',
            content: "The account couldn't be removed, please try again later",
          });
        }
        this.setState({ isSaving: false });
      });
    }
  }

  private updateAgency = (agency: AgencyData) => {
    agency.defaultPermissionsCreatingAccount = this.reducedPermissions(agency.defaultPermissionsCreatingAccount);
    agency.defaultPermissionsCreatingAddress = this.reducedPermissions(agency.defaultPermissionsCreatingAddress);
    agency.defaultPermissionsCreatingPatient = this.reducedPermissions(agency.defaultPermissionsCreatingPatient);
    agency.defaultPermissionsCreatingPayment = this.reducedPermissions(agency.defaultPermissionsCreatingPayment);

    this.setState({ isSaving: true });
    HealAPI.updateAgency(agency).then((response) => {
      const updatedAgency = response.data?.data;
      if (response.ok && updatedAgency) {
        this.props.actions.toast({
          type: 'success',
          title: 'Success',
          content: 'The agency was successfully updated',
        });
        if (this.props.agencies) {
          const agencies = this.props.agencies.slice();
          const agencyIndex = agencies.findIndex((g) => g.id === agency.id);
          agencies[agencyIndex] = { ...agencies[agencyIndex], ...response.data?.data };
          this.props.actions.setAgencies(agencies);
        }

        this.setState({
          pristineAgency: agency,
          agency: _.cloneDeep(agency),
        });
      }
      this.setState({ isSaving: false });
    });
  };

  private reducedPermissions(permissions: string[]) {
    if (permissions.length === RESOURCE_PERMISSION_TYPES.length - 1) {
      return ['ALL'];
    }
    return permissions;
  }

  private buildAgentColumns(): IColumn[] {
    return [
      {
        key: 'name',
        name: 'Agent Name',
        minWidth: 80,
        maxWidth: 320,
        onRender: (item: ConciergeAgent) => {
          const nameDisplay = `${item.lastName}, ${item.firstName}`;
          const onRenderPrimaryText = () => {
            return <span>{nameDisplay}</span>;
          };
          return <Persona size={PersonaSize.size24} text={nameDisplay} onRenderPrimaryText={onRenderPrimaryText} />;
        },
      },
      {
        key: 'username',
        name: 'Email',
        minWidth: 320,
        maxWidth: 320,
        fieldName: 'username',
      },
      {
        key: 'phoneNumber',
        name: 'Phone Number',
        minWidth: 120,
        maxWidth: 120,
        fieldName: 'phoneNumber',
        onRender: (item: ConciergeAgent) => {
          return item ? formatPhoneNumber(item.phoneNumber) : '';
        },
      },
      {
        key: 'defaultPromoCode',
        name: 'Promo Code',
        minWidth: 120,
        maxWidth: 120,
        fieldName: 'defaultPromoCode',
      },
      {
        key: 'remove',
        name: 'Remove',
        minWidth: 80,
        maxWidth: 320,
        onRender: (item: ConciergeAgent) => {
          return <IconButton iconProps={{ iconName: 'Delete' }} onClick={() => this.onClickRemoveAgent(item)} />;
        },
      },
    ];
  }

  private onClickRemoveAgent(agent: ConciergeAgent) {
    const paymentMethods: AgencyPayment[] = this.state.payments.data?.filter((p) => p.userAccountId === agent.id) || [];

    OkCancelDialog.show({
      title: 'Remove Agent',
      subText: 'Are you sure you want to remove this agent?',
      warning: paymentMethods.length > 0 ? 'This action will also remove all payment methods associated with this agent' : undefined,
      onOk: () => this.removeConciergeAgent(this.props.params.id, agent.id, paymentMethods),
    });
  }

  private buildAddressColumns(): IColumn[] {
    return [
      {
        key: 'streetAddress',
        name: 'Address',
        minWidth: 80,
        maxWidth: 320,
        fieldName: 'streetAddress',
      },
      {
        key: 'instructions',
        name: 'Entry Instructions',
        minWidth: 320,
        maxWidth: 320,
        fieldName: 'instructions',
        isMultiline: true,
      },
      {
        key: 'city',
        name: 'City',
        minWidth: 120,
        maxWidth: 120,
        fieldName: 'city',
      },

      {
        key: 'addressType',
        name: 'Address Type',
        minWidth: 120,
        maxWidth: 120,
        fieldName: 'addressType',
        onRender: (item: AgencyAddress) => {
          const addressType: IDropdownOption | undefined = PARTNER_ADDRESS_TYPE.find((t) => t.key === item.addressType);
          return addressType ? addressType.text : item.addressType;
        },
      },
      {
        key: 'remove',
        name: 'Remove',
        minWidth: 80,
        maxWidth: 320,
        onRender: (item: AgencyAddress) => {
          return (
            <IconButton
              iconProps={{ iconName: 'Delete' }}
              onClick={() =>
                OkCancelDialog.show({
                  title: 'Remove Address',
                  subText: 'Are you sure you want to remove this address?',
                  onOk: () => this.removeAgencyAddress(this.props.params.id, item.id),
                })
              }
            />
          );
        },
      },
    ];
  }

  private buildServiceColumns(): IColumn[] {
    return [
      {
        key: 'name',
        name: 'Service',
        minWidth: 200,
        maxWidth: 200,
        fieldName: 'name',
      },
      {
        key: 'code',
        name: 'Code',
        minWidth: 160,
        maxWidth: 160,
        fieldName: 'code',
      },
      {
        key: 'basePrice',
        name: 'Base price',
        minWidth: 160,
        maxWidth: 160,
        onRender: (item: AgencyService) => {
          return (
            <input
              key={item.code}
              defaultValue={item.basePrice?.toFixed(2)}
              placeholder="(default)"
              prefix="$"
              min={0}
              maxLength={6}
              onKeyPress={(e) => {
                const regex = /^[0-9.]$/;
                const key = String.fromCharCode(e.keyCode || e.which);
                if (!regex.test(key)) {
                  e.preventDefault();
                }
              }}
              onBlur={(e) => {
                let price = e.target.value ? parseFloat(e.target.value) : undefined;
                // Truncate to two decimal places to avoid saving too much precision
                if (price) {
                  price = Math.floor(price * 100) / 100;
                }
                e.target.value = price?.toFixed(2) || '';
                this.updateServiceBasePrice(item.code, price);
              }}
              style={{ width: 100, background: 'none', border: 'none', outline: 'none' }}
            />
          );
        },
      },
      {
        key: 'enabled',
        name: 'Enabled',
        minWidth: 80,
        maxWidth: 80,
        onRender: (item: AgencyService) => {
          return <Checkbox checked={!item.isBlocked} onChange={() => this.updateServiceAllowance(item.code, !item.isBlocked)} />;
        },
      },
    ];
  }

  private updateServiceAllowance(serviceCode: string, shouldBlock: boolean) {
    if (!this.state.agency) {
      return;
    }
    const serviceConfigMap = _.cloneDeep(this.state.agency.serviceConfigMap);

    if (serviceConfigMap[serviceCode] == null) {
      serviceConfigMap[serviceCode] = { isBlocked: shouldBlock };
    } else {
      serviceConfigMap[serviceCode].isBlocked = shouldBlock;
    }

    this.setState({
      agency: { ...this.state.agency, serviceConfigMap: serviceConfigMap },
    });
  }

  private updateServiceBasePrice(serviceCode: string, price?: number) {
    if (!this.state.agency) {
      return;
    }
    const serviceConfigMap = _.cloneDeep(this.state.agency.serviceConfigMap);

    if (serviceConfigMap[serviceCode] == null) {
      serviceConfigMap[serviceCode] = {
        basePrice: price,
        isBlocked: false,
      };
    } else {
      serviceConfigMap[serviceCode].basePrice = price;
    }

    this.setState({
      agency: { ...this.state.agency, serviceConfigMap: serviceConfigMap },
    });
  }
}

export function mapStateToProps(state: RootState): StateProps {
  return {
    agencies: state.concierge.agencies,
    isFetching: state.concierge.isFetching,
    services: state.serviceOffering.services.data || [],
  };
}

export function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    actions: bindActionCreators({ onPageDidMount, onPageWillUnmount, setAgencies, toast }, dispatch),
  };
}

export const AgencyContainer = withRouter(connect<StateProps, DispatchProps, OwnProps, RootState>(mapStateToProps, mapDispatchToProps)(Component));
